/* Leave Applications — Super Admin / Major Admin / Admin approval section.
   Shows every detail: type, dates, days, reason, handover, contacts, returning
   date, name, designation, department — plus a log of who granted each one. */
import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { Check, X as XIcon, Phone, CalendarDays, Clock } from "lucide-react";
import Paginated from "../../components/Paginated";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  reverted: "bg-slate-100 text-slate-600",
};

function Detail({ label, value }) {
  return (
    <div>
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function ApplicationCard({ a, canDecide, canRevert, onDecide, onRevert }) {
  const [note, setNote] = useState("");
  const isHalf = a.leave_type === "half_day";
  return (
    <div className="bg-white border border-line rounded-xl overflow-hidden" data-testid={`leave-approval-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-line/60 bg-pearl/50">
        <div className="flex items-center gap-3">
          {isHalf ? <Clock size={16} className="text-gold"/> : <CalendarDays size={16} className="text-gold"/>}
          <b className="font-display text-lg text-maroon">{a.name}</b>
          <span className="text-xs text-ash">{a.designation} · {a.department}</span>
        </div>
        <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_BADGE[a.status]}`}>{a.status}</span>
      </div>

      <div className="p-5 grid sm:grid-cols-3 gap-x-6 gap-y-3">
        <Detail label="Type" value={isHalf ? "Half Day" : "Holiday / Leave"} />
        <Detail label={isHalf ? "Half day on" : "From → To"} value={isHalf ? a.half_day_date : `${a.from_date} → ${a.to_date}`} />
        <Detail label="Days" value={`${a.days}`} />
        <Detail label="Applied on" value={a.applied_on} />
        <Detail label="Coming back on" value={a.returning_on} />
        <Detail label="Store" value={a.store} />
        <div className="sm:col-span-3"><Detail label="Reason" value={a.reason} /></div>
        <div className="sm:col-span-3"><Detail label="Works in hand (handed over)" value={a.works_in_hand} /></div>
        <Detail label="Task assigned to" value={`${a.handover_to_name} (${a.handover_store})`} />
        <Detail label="Contact" value={<span className="inline-flex items-center gap-1"><Phone size={11}/>{a.contact_1}{a.contact_2 ? ` / ${a.contact_2}` : ""}</span>} />
        <Detail label="Applicant role" value={(a.role || "").replace("_", " ")} />
      </div>

      {a.status === "pending" && canDecide && (
        <div className="px-5 pb-5 flex flex-wrap items-center gap-2">
          <input placeholder="Note (optional)" className="flex-1 min-w-[180px] border border-line rounded-full px-4 py-2 bg-pearl text-sm" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`leave-note-${a.id}`}/>
          <button onClick={() => onDecide(a.id, "approved", note)} className="inline-flex items-center gap-1.5 bg-green-700 text-white rounded-full px-4 py-2 text-xs hover:bg-green-800 transition-colors" data-testid={`leave-approve-${a.id}`}>
            <Check size={13}/> Grant Permission
          </button>
          <button onClick={() => onDecide(a.id, "rejected", note)} className="inline-flex items-center gap-1.5 bg-red-600 text-white rounded-full px-4 py-2 text-xs hover:bg-red-700 transition-colors" data-testid={`leave-reject-${a.id}`}>
            <XIcon size={13}/> Reject
          </button>
        </div>
      )}

      {a.status !== "pending" && (
        <div className="px-5 pb-4 text-[11px] text-ash border-t border-line/60 pt-3">
          {/* Permission log — visible to super admin, major admin & admin */}
          <b>{a.status === "rejected" ? "Rejected" : "Permission granted"}</b> by <b className="text-maroon">{a.decided_by_name}</b> ({(a.decided_by_role || "").replace("_", " ")}) on {a.decided_at ? new Date(a.decided_at).toLocaleString() : "—"}
          {a.decision_note && <> — “{a.decision_note}”</>}
          {a.status === "approved" && <div className="mt-1">The handed-over works were added to <b>{a.handover_to_name}</b>'s task list automatically.</div>}
          {a.status === "reverted" && (
            <div className="mt-1 text-slate-600">
              <b>Marked as NOT taken</b> by <b>{a.reverted_by_name}</b> (super admin) on {a.reverted_at ? new Date(a.reverted_at).toLocaleString() : "—"} — “{a.revert_note}”. The leave / half-day counters were reduced automatically.
            </div>
          )}
          {a.status === "approved" && canRevert && (
            <button onClick={() => onRevert(a)} className="mt-2 inline-flex items-center gap-1.5 border border-slate-400 text-slate-700 rounded-full px-3 py-1.5 text-[11px] hover:bg-slate-100 transition-colors" data-testid={`leave-revert-${a.id}`}>
              Leave / half day was NOT taken — Revert
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function LeaveApprovals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const canDecide = hasRole(user, ["super_admin", "major_admin", "admin"]);
  const canRevert = hasRole(user, "super_admin"); // ONLY super admin can revert

  const load = () => api.get("/api/crm/leave-applications").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (id, status, note) => {
    try {
      await api.patch(`/api/crm/leave-applications/${id}/decision`, { status, note });
      toast.success(status === "approved" ? "Permission granted — tasks handed over" : "Application rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revert = async (a) => {
    const note = window.prompt(`Confirm: ${a.name} did NOT take this ${a.leave_type === "half_day" ? "half day" : "leave"}?\nOptional note:`, "Leave was not taken");
    if (note === null) return;
    try {
      await api.patch(`/api/crm/leave-applications/${a.id}/revert`, { note });
      toast.success("Reverted — counters reduced on the salesperson's dashboard");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = tab === "all" ? items : items.filter((i) => i.status === tab);
  const counts = {
    pending: items.filter((i) => i.status === "pending").length,
    approved: items.filter((i) => i.status === "approved").length,
    rejected: items.filter((i) => i.status === "rejected").length,
    reverted: items.filter((i) => i.status === "reverted").length,
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Approvals</div>
        <h1 className="font-display text-4xl text-maroon">Leave Applications</h1>
        <p className="text-xs text-ash mt-1">Half-day and leave applications from the team. Grant or reject — every decision is logged with who gave the permission.</p>
      </div>

      <div className="flex border border-line rounded-full overflow-hidden bg-white w-fit mb-6">
        {[["pending", `Pending (${counts.pending})`], ["approved", `Approved (${counts.approved})`], ["rejected", `Rejected (${counts.rejected})`], ["reverted", `Not Taken (${counts.reverted})`], ["all", "All"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-xs transition-colors ${tab === k ? "bg-maroon text-pearl" : "hover:bg-pearl"}`} data-testid={`leave-tab-${k}`}>{l}</button>
        ))}
      </div>

      <Paginated items={filtered} pageSize={6}>
        {(pageItems) => (
          <div className="space-y-4">
            {pageItems.map((a) => <ApplicationCard key={a.id} a={a} canDecide={canDecide} canRevert={canRevert} onDecide={decide} onRevert={revert} />)}
          </div>
        )}
      </Paginated>
      {filtered.length === 0 && <div className="text-center text-sm text-ash py-16 bg-white border border-line rounded-xl">No {tab !== "all" ? tab : ""} applications.</div>}
    </div>
  );
}
